import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Send, Bell, X, RotateCcw, Pencil, Trash2, Check, Smartphone, Mail, MessageSquare } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/utils'
import { useAuth } from '../context/AuthContext'
import { DeepLinkInput } from '../components/DeepLinkInput'

type Channel = 'push' | 'sms' | 'email'

interface NotificationLog {
  id: string
  title: string
  body: string
  target: string
  category: string | null
  sent_count: number
  link: string | null
  created_at: string
  sent_by_name: string
  channels: string | null
  push_count: number | null
  sms_count: number | null
  email_count: number | null
}

interface AudiencePreview {
  audience_size: number
  push: number
  sms: number
  email: number
}

const TARGETS = [
  { value: 'all',     label: 'All users' },
  { value: 'free',    label: 'Free plan users only' },
  { value: 'premium', label: 'Premium users only' },
]

const CATEGORIES = [
  { value: '',              label: 'Broadcast (all categories)' },
  { value: 'order_updates', label: 'Order Updates' },
  { value: 'promotions',    label: 'Promotions & Deals' },
  { value: 'new_arrivals',  label: 'New Arrivals' },
]

const CATEGORY_BADGE: Record<string, { label: string; className: string }> = {
  order_updates: { label: 'Order Updates',      className: 'bg-blue-500/15 text-blue-400' },
  promotions:    { label: 'Promotions & Deals', className: 'bg-amber-500/15 text-amber-400' },
  new_arrivals:  { label: 'New Arrivals',        className: 'bg-green-500/15 text-green-400' },
}

// Shared modal shell
function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2e2e2e]">
          <h3 className="text-sm font-semibold text-white">{title}</h3>
          <button onClick={onClose} className="text-gray-600 hover:text-white transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  )
}

// Delete confirmation modal
function DeleteModal({
  log,
  onClose,
  onDeleted,
}: {
  log: NotificationLog
  onClose: () => void
  onDeleted: () => void
}) {
  const del = useMutation({
    mutationFn: () => api.delete(`/notifications/${log.id}`),
    onSuccess: () => { onDeleted(); onClose() },
  })

  return (
    <Modal title="Delete Notification" onClose={onClose}>
      <p className="text-sm text-gray-400 mb-1">
        Are you sure you want to delete this notification record?
      </p>
      <p className="text-sm text-white font-medium mb-4">"{log.title}"</p>
      <p className="text-xs text-gray-600 mb-5">
        This only removes the log entry — it does not unsend the notification.
      </p>

      {del.isError && (
        <p className="text-red-400 text-sm bg-red-400/10 rounded-lg px-3 py-2 mb-3">
          Failed to delete. Please try again.
        </p>
      )}

      <div className="flex gap-2">
        <button
          onClick={onClose}
          className="flex-1 bg-[#2a2a2a] hover:bg-[#333] text-gray-300 text-sm font-medium px-4 py-2.5 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={() => del.mutate()}
          disabled={del.isPending}
          className="flex-1 flex items-center justify-center gap-2 bg-red-700 hover:bg-red-800 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2.5 rounded-lg transition-colors"
        >
          <Trash2 size={14} />
          {del.isPending ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </Modal>
  )
}

function ChannelChip({
  active, onClick, icon, label, count, warning,
}: {
  active: boolean; onClick: () => void; icon: React.ReactNode; label: string
  count: number | null; warning?: string | null
}) {
  return (
    <button
      onClick={onClick}
      title={warning ?? undefined}
      className={`flex flex-col items-start gap-1 rounded-lg border px-3 py-2.5 text-left transition-colors ${
        active
          ? 'bg-[#C41E3A]/10 border-[#C41E3A]/40 text-white'
          : 'bg-[#111] border-[#2e2e2e] text-gray-400 hover:text-gray-200 hover:border-[#3a3a3a]'
      }`}
    >
      <div className="flex items-center gap-2">
        <span className={active ? 'text-[#C41E3A]' : 'text-gray-500'}>{icon}</span>
        <span className="text-sm font-medium">{label}</span>
      </div>
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span>{count !== null ? count : '—'} recipients</span>
        {warning && active && <span className="text-amber-400">$</span>}
      </div>
    </button>
  )
}

function ChannelCounts({ row }: { row: NotificationLog }) {
  // Legacy rows (pre-channels column) use total sent_count against push.
  const push  = row.push_count  ?? row.sent_count
  const sms   = row.sms_count   ?? 0
  const email = row.email_count ?? 0
  const parts: string[] = []
  if (push  > 0) parts.push(`📱 ${push}`)
  if (sms   > 0) parts.push(`💬 ${sms}`)
  if (email > 0) parts.push(`✉️ ${email}`)
  return <span className="text-xs">{parts.length > 0 ? parts.join(' · ') : row.sent_count}</span>
}

function CategoryBadge({ category }: { category: string | null }) {
  if (!category) return <span className="text-gray-700 text-xs">—</span>
  const badge = CATEGORY_BADGE[category]
  if (!badge) return <span className="text-gray-500 text-xs">{category}</span>
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${badge.className}`}>
      {badge.label}
    </span>
  )
}

export default function Notifications() {
  const { can } = useAuth()
  const isSuperAdmin = can(['super_admin'])

  const composeRef = useRef<HTMLDivElement>(null)

  const [title,    setTitle]    = useState('')
  const [body,     setBody]     = useState('')
  const [target,   setTarget]   = useState('all')
  const [category, setCategory] = useState('')
  const [link,     setLink]     = useState('')
  const [channels, setChannels] = useState<Channel[]>(['push'])
  const [sent,     setSent]     = useState(false)
  const [loadedFrom, setLoadedFrom] = useState<string | null>(null)

  // Audience preview — fetches on target / category / channels change so
  // ops sees "This will reach 220 push devices, 200 SMS, 180 emails"
  // before they hit send. Debounced by react-query's stale time.
  const [preview, setPreview] = useState<AudiencePreview | null>(null)
  useEffect(() => {
    let cancelled = false
    api.post('/notifications/preview', { target, ...(category ? { category } : {}), channels })
      .then(r => { if (!cancelled) setPreview(r.data as AudiencePreview) })
      .catch(() => {/* silent */})
    return () => { cancelled = true }
  }, [target, category, channels])

  function toggleChannel(ch: Channel) {
    setChannels(prev => prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch])
  }

  const [deletingLog,  setDeletingLog]  = useState<NotificationLog | null>(null)
  const [resendingId,  setResendingId]  = useState<string | null>(null)
  const [resentId,     setResentId]     = useState<string | null>(null)

  function loadIntoComposer(log: NotificationLog) {
    setTitle(log.title)
    setBody(log.body)
    setTarget(log.target)
    setCategory(log.category ?? '')
    setLink(log.link ?? '')
    const parsed = (log.channels ?? 'push').split(',').filter(
      (c): c is Channel => ['push', 'sms', 'email'].includes(c)
    )
    setChannels(parsed.length > 0 ? parsed : ['push'])
    setLoadedFrom(log.id)
    setSent(false)
    composeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const { data: logs, refetch } = useQuery<{ notifications: NotificationLog[] }>({
    queryKey: ['notification-logs'],
    queryFn: () => api.get('/notifications/logs').then(r => r.data),
  })

  const send = useMutation({
    mutationFn: () => api.post('/notifications/send', {
      title,
      body,
      target,
      channels,
      ...(category ? { category } : {}),
      ...(link.trim() ? { link: link.trim() } : {}),
    }),
    onSuccess: () => {
      setSent(true)
      setTitle('')
      setBody('')
      setTarget('all')
      setCategory('')
      setLink('')
      setChannels(['push'])
      setLoadedFrom(null)
      refetch()
      setTimeout(() => setSent(false), 4000)
    },
  })

  async function handleResend(log: NotificationLog) {
    setResendingId(log.id)
    try {
      await api.post(`/notifications/${log.id}/resend`, {})
      await refetch()
      setResentId(log.id)
      setTimeout(() => setResentId(null), 3000)
    } catch {
      // silent — user can retry
    } finally {
      setResendingId(null)
    }
  }

  const canSend = title.trim().length > 0 && body.trim().length > 0 && channels.length > 0

  return (
    <div className="p-6">
      <h1 className="text-lg font-semibold text-white mb-1">Mass Notifications</h1>
      <p className="text-sm text-gray-500 mb-6">
        Broadcast to customers across push, SMS, and email. Category-opt-out settings on each user apply to every channel.
      </p>

      {/* Compose */}
      <div ref={composeRef} className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl p-5 mb-6">
        <h2 className="text-sm font-medium text-gray-300 mb-4 flex items-center gap-2">
          <Bell size={15} className="text-[#C41E3A]" />
          Compose
        </h2>

        {loadedFrom && (
          <div className="flex items-center justify-between text-xs text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded-lg px-3 py-2 mb-3">
            <span>Loaded from history — edit and send as a new notification.</span>
            <button
              onClick={() => { setLoadedFrom(null); setTitle(''); setBody(''); setTarget('all'); setCategory(''); setLink('') }}
              className="text-amber-500 hover:text-amber-300 transition-colors ml-3 shrink-0"
            >
              <X size={12} />
            </button>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Title</label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              maxLength={65}
              placeholder="e.g. Weekend Special 🍷"
              className="w-full bg-[#111] border border-[#2e2e2e] rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C41E3A]"
            />
            <p className="text-xs text-gray-600 mt-1 text-right">{title.length}/65</p>
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-1 block">Message</label>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              maxLength={200}
              rows={3}
              placeholder="e.g. Get 15% off all wines this weekend. Shop now!"
              className="w-full bg-[#111] border border-[#2e2e2e] rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C41E3A] resize-none"
            />
            <p className="text-xs text-gray-600 mt-1 text-right">{body.length}/200</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Audience</label>
              <select
                value={target}
                onChange={e => setTarget(e.target.value)}
                className="w-full bg-[#111] border border-[#2e2e2e] rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
              >
                {TARGETS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            <div>
              <label className="text-xs text-gray-500 mb-1 block">
                Category
                <span className="text-gray-700 ml-1">(controls opt-out filtering)</span>
              </label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className="w-full bg-[#111] border border-[#2e2e2e] rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
              >
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-2 block">
              Deep Link <span className="text-gray-700">(optional — where to take the user on tap)</span>
            </label>
            <DeepLinkInput value={link} onChange={setLink} />
          </div>

          <div>
            <label className="text-xs text-gray-500 mb-2 block">Channels</label>
            <div className="grid grid-cols-3 gap-2">
              <ChannelChip
                active={channels.includes('push')}
                onClick={() => toggleChannel('push')}
                icon={<Smartphone size={14} />}
                label="Push"
                count={preview?.push ?? null}
              />
              <ChannelChip
                active={channels.includes('sms')}
                onClick={() => toggleChannel('sms')}
                icon={<MessageSquare size={14} />}
                label="SMS"
                count={preview?.sms ?? null}
                warning={channels.includes('sms') ? `Twilio charges per SMS — ${preview?.sms ?? '?'} messages will be sent` : null}
              />
              <ChannelChip
                active={channels.includes('email')}
                onClick={() => toggleChannel('email')}
                icon={<Mail size={14} />}
                label="Email"
                count={preview?.email ?? null}
              />
            </div>
            {preview && (
              <p className="text-xs text-gray-500 mt-2">
                Audience: <span className="text-gray-300">{preview.audience_size}</span> users matched
                {channels.includes('sms') && preview.sms > 0 && (
                  <span> · SMS batch will land in ~{Math.max(1, Math.ceil(preview.sms / 10))}s</span>
                )}
              </p>
            )}
          </div>

          {sent && (
            <div className="text-green-400 text-sm bg-green-400/10 rounded-lg px-4 py-3">
              Notification sent successfully.
            </div>
          )}
          {send.isError && (
            <div className="text-red-400 text-sm bg-red-400/10 rounded-lg px-4 py-3">
              Failed to send. Please try again.
            </div>
          )}

          <button
            onClick={() => send.mutate()}
            disabled={!canSend || send.isPending}
            className="flex items-center gap-2 bg-[#C41E3A] hover:bg-[#a01830] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2.5 rounded-lg transition-colors"
          >
            <Send size={14} />
            {send.isPending ? 'Sending…' : 'Send Notification'}
          </button>
        </div>
      </div>

      {/* History */}
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden overflow-x-auto">
        <div className="px-5 py-4 border-b border-[#2e2e2e]">
          <h2 className="text-sm font-medium text-gray-300">Sent History</h2>
        </div>
        {!logs?.notifications?.length ? (
          <p className="px-5 py-6 text-sm text-gray-600">No notifications sent yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-600 border-b border-[#2e2e2e]">
                {['Title', 'Message', 'Category', 'Audience', 'Link', 'Delivered', 'By', 'Date', ''].map((h, i) => (
                  <th key={i} className="px-4 py-3 text-left font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.notifications.map(n => (
                <tr key={n.id} className="border-b border-[#222] group">
                  <td className="px-4 py-3 text-white font-medium">{n.title}</td>
                  <td className="px-4 py-3 text-gray-400 max-w-[180px] truncate">{n.body}</td>
                  <td className="px-4 py-3 whitespace-nowrap"><CategoryBadge category={n.category} /></td>
                  <td className="px-4 py-3 text-gray-400 capitalize">{n.target.replace(/_/g, ' ')}</td>
                  <td className="px-4 py-3 text-gray-600 font-mono text-xs max-w-[140px] truncate">
                    {n.link ?? <span className="text-gray-700">—</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-400 whitespace-nowrap">
                    <ChannelCounts row={n} />
                  </td>
                  <td className="px-4 py-3 text-gray-400">{n.sent_by_name}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{formatDateTime(n.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      {/* Resend */}
                      <button
                        onClick={() => handleResend(n)}
                        disabled={resendingId === n.id}
                        title="Resend"
                        className="p-1.5 rounded-md text-gray-600 hover:text-white hover:bg-[#2e2e2e] transition-colors disabled:opacity-40"
                      >
                        {resentId === n.id
                          ? <Check size={14} className="text-green-400" />
                          : <RotateCcw size={14} className={resendingId === n.id ? 'animate-spin' : ''} />
                        }
                      </button>

                      {/* Load into composer */}
                      <button
                        onClick={() => loadIntoComposer(n)}
                        title="Load into composer"
                        className="p-1.5 rounded-md text-gray-600 hover:text-white hover:bg-[#2e2e2e] transition-colors"
                      >
                        <Pencil size={14} />
                      </button>

                      {/* Delete — super_admin only */}
                      {isSuperAdmin && (
                        <button
                          onClick={() => setDeletingLog(n)}
                          title="Delete"
                          className="p-1.5 rounded-md text-gray-600 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modals */}
      {deletingLog && (
        <DeleteModal
          log={deletingLog}
          onClose={() => setDeletingLog(null)}
          onDeleted={() => refetch()}
        />
      )}
    </div>
  )
}
